import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import AppLayout from './AppLayout';

const ROLE_LABELS = {
  candidate: 'Candidate',
  voucher: 'Voucher',
  hiring_manager: 'Hiring Manager',
};

export default function DemoRoleRoute({ role, email, password }) {
  const { user, profile, loading, signIn, signOut, switchRole, addRole, refreshProfile } = useAuth();
  const { loadDemoData } = useProfile();
  const [phase, setPhase] = useState('signing-in');
  const [statusMsg, setStatusMsg] = useState('');
  const running = useRef(false);

  // If user is set but profile failed to load (406/RLS timing), actively retry
  useEffect(() => {
    if (!user || profile || loading) return;
    const timer = setInterval(() => {
      if (!profile) refreshProfile();
    }, 1000);
    return () => clearInterval(timer);
  }, [user, profile, loading, refreshProfile]);

  useEffect(() => {
    if (loading || running.current) return;
    // Wait for profile to load before making decisions about the user
    if (user && !profile) return;

    running.current = true;

    async function setup() {
      try {
        // Step 1: Ensure correct user is signed in
        if (user && profile?.email !== email) {
          setStatusMsg('Switching accounts...');
          await signOut();
          running.current = false; // Will re-run after signOut clears state
          return;
        }

        if (!user) {
          setStatusMsg(`Signing in as ${ROLE_LABELS[role]}...`);
          await signIn(email, password);
          // signIn triggers onAuthStateChange → setUser → fetchProfile
          // Wait for that by returning; this effect re-runs when profile loads
          running.current = false;
          return;
        }

        // Step 2: Set role
        setStatusMsg('Setting up role...');
        await addRole(role).catch(() => {});
        switchRole(role);

        // Step 3: Load demo data if needed
        if (!profile.linkedin_connected) {
          setStatusMsg(`Loading demo data for ${ROLE_LABELS[role]}...`);
          setPhase('loading-data');
          const { error } = await loadDemoData();
          if (error) console.error(`[Demo:${role}] loadDemoData error:`, error);

          // Force refresh profile from Supabase to get updated linkedin_connected
          await refreshProfile();
        }

        setPhase('ready');
      } catch (err) {
        console.error(`[Demo:${role}] setup error:`, err.message);
        setPhase('ready'); // Show app anyway on error
      }
    }

    setup();
  }, [user, profile, loading, email, password, role, signIn, signOut, switchRole, addRole, loadDemoData, refreshProfile]);

  if (phase !== 'ready') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0B1F3B 0%, #122B4F 50%, #18B6B2 100%)',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            color: 'white',
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '0.5rem'
          }}>
            Loading VOUCH...
          </div>
          <div style={{
            color: 'rgba(255,255,255,0.6)',
            fontSize: '0.875rem'
          }}>
            {statusMsg || `Signing in as ${ROLE_LABELS[role]}`}
          </div>
        </div>
      </div>
    );
  }

  return <AppLayout />;
}
